import { alpha, ButtonBase, CircularProgress } from "@mui/material";
import Spacer from "atoms/Spacer";
import Stack from "atoms/Stack";
import VideoPlayer from "atoms/VideoPlayer";
import { COLOR_BLACK, COLOR_SECONDARY, COLOR_WHITE } from "helpers/colors";
import { VIDEO_PLAYER_RATIO } from "helpers/constants";
import { ONSEN_APP } from "helpers/environment";
import { fetchFile } from "helpers/fetchFile";
import { SPACING, SPACING_HALF } from "helpers/spacings";
import useFullscreenState from "helpers/useFullscreenState";
import useMobileLayout from "helpers/useMobileLayout";
import { progressBar } from "helpers/useProgressBar";
import useShowMessage from "helpers/useShowMessage";
import { ContentCopy, Download, Link, Play, Share, ShareVariant } from "mdi-material-ui";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useComponentSize, useWindowSize } from "react-use-size";

import FullscreenButton from "./FullscreenButton";
import MuteButton from "./MuteButton";
import PlayerButton from "./PlayerButton";
import ShareMenuButton from "./ShareMenuButton";
import VolumeButton from "./VolumeButton";

// Tall enough to grab with a finger; the bar drawn inside it is thinner
const SEEK_BAR_HIT_HEIGHT = 14;
// The largest 16:9 box that fits its container
const FITTED_VIDEO_WIDTH = `min(100cqw, ${VIDEO_PLAYER_RATIO * 100}cqh)`;
// Touch screens wider than the mobile layout (eg a landscape iPad) have no hover either
const CAN_HOVER = !!window.matchMedia?.("(hover: hover)").matches;

export default function ReelVideoPlayer({
  title,
  header,
  footer,
  poster,
  videoUrl,
  shareUrl,
  videoPending = false,
  vidoeUnavailable = false,
  onDownload,
  onVideoPlay,
  onShared,
  sharingDisabled = false,
  dragToSeek = true,
  tip,
}) {
  const [fullscreen] = useFullscreenState();
  // mute and volume stay hidden until the video is found to have audio, which Chrome only knows once it plays
  const [audioEnabled, audioEnabledSet] = useState(false);
  const [buffering, bufferingSet] = useState(false);
  const [playing, playingSet] = useState(false);
  const [playingProgress, playingProgressSet] = useState(0);
  const [hovering, hoveringSet] = useState(false);
  const [seeking, seekingSet] = useState(false);
  const [shareMenuOpened, shareMenuOpenedSet] = useState(false);
  const [fetchFileResult, fetchFileResultSet] = useState(null);
  const videoRef = useRef();
  const videoBoxSize = useComponentSize();
  const playedRef = useRef(false);

  const windowSize = useWindowSize();
  const mobileLayout = useMobileLayout();
  const sharing = !sharingDisabled;
  // the share sheet is only there on https, and not in Firefox or the Android app
  const shareSheet = !!window?.navigator?.share;

  // Download and share need the whole file, fetched up front as the share sheet has to open within the tap.
  // Without a share sheet, mobile's menu only needs it for download, so fetches it once opened.
  const fileWanted = sharing && (!mobileLayout || shareSheet || shareMenuOpened);
  useEffect(() => {
    if (!fileWanted || !title || !videoUrl) return;

    const controller = new AbortController();
    let fetched;
    fetchFile(videoUrl, title, { signal: controller.signal }).then((result) => {
      // closed or changed while it finished
      if (controller.signal.aborted) {
        if (result.fileObjectUrl) URL.revokeObjectURL(result.fileObjectUrl);
        return;
      }
      fetched = result;
      fetchFileResultSet(result);
    });

    // swiping through highlights would otherwise keep every one's download going and its file in memory
    return () => {
      controller.abort();
      if (fetched?.fileObjectUrl) URL.revokeObjectURL(fetched.fileObjectUrl);
      fetchFileResultSet(null);
    };
  }, [fileWanted, title, videoUrl]);

  useLayoutEffect(() => {
    bufferingSet(true);
  }, [videoUrl]);

  const { file, filename, fileObjectUrl } = fetchFileResult || {};

  const showMessage = useShowMessage();
  const smallScreen = windowSize.height < 400;
  const restrictSize = !smallScreen && !fullscreen;
  // a landscape phone gives the video the full height, with its controls in the black beside it
  const mobileLandscape = mobileLayout && !restrictSize;
  const controlGroupStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: alpha(COLOR_BLACK, 0.5),
    borderRadius: 999,
  };
  // Mobile has no mouse to hover with, so its seek bar always shows
  const controlsVisible = mobileLayout || !CAN_HOVER || hovering || seeking;

  const download = () => {
    const a = document.createElement("a");
    a.download = filename;
    a.href = fileObjectUrl;
    a.click();
    onShared?.({ share_destination: "download" });
    onDownload?.();
  };
  // the clipboard is only there on https
  const copyLink =
    !!window?.navigator?.clipboard &&
    (() =>
      progressBar(async () => {
        await window.navigator.clipboard.writeText(shareUrl);
        await showMessage("Link copied to clipboard.");
        onShared?.({ share_destination: "copy_link" });
      }));
  // the video and its link, so the sheet can save or send the video; just the link before the file is ready, or if it
  // couldn't be fetched
  const share =
    shareSheet &&
    (() =>
      progressBar(async () => {
        const data = file ? { files: [file], url: shareUrl } : { url: shareUrl };
        try {
          await window.navigator.share(window.navigator.canShare?.(data) === false ? { url: shareUrl } : data);
        } catch (error) {
          // closing the sheet without sharing
          if (error.name === "AbortError") return;
          throw error;
        }
        onShared?.({ share_destination: "web_share" });
      }));

  const mobileControls = (color) => (
    <>
      {audioEnabled && <MuteButton color={color} />}
      {sharing &&
        (share ? (
          <PlayerButton title="Share" color={color} onClick={share} icon={<ShareVariant />} />
        ) : (
          (copyLink || !ONSEN_APP) && (
            <ShareMenuButton
              color={color}
              fileReady={!!file}
              onOpen={() => shareMenuOpenedSet(true)}
              onCopyLink={copyLink}
              onDownload={!ONSEN_APP && download}
            />
          )
        ))}
      {/* a phone's own player fills the screen and rotates; the app instead rotates to landscape itself */}
      <FullscreenButton color={color} video={videoRef.current} preferVideo={!ONSEN_APP} />
    </>
  );

  const seekTo = (event) => {
    const video = videoRef.current;
    if (!video?.duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    video.currentTime = ratio * video.duration;
    playingProgressSet(ratio);
  };

  return (
    <div
      style={{
        flex: "1 1 auto",
        backgroundColor: COLOR_BLACK,
        color: COLOR_WHITE,
        display: "flex",
        justifyContent: "stretch",
        alignItems: "stretch",
        ...(restrictSize && {
          justifyContent: "center",
          alignItems: "center",
        }),
      }}
    >
      <div
        style={{
          flex: "1 1 auto",
          // lets the header's long names ellipsise instead of widening the video past the screen
          minWidth: 0,
          display: "flex",
          flexFlow: "column nowrap",
          justifyContent: "stretch",
          alignItems: "stretch",
          ...(restrictSize && {
            maxWidth: 1000,
          }),
          position: "relative",
        }}
      >
        {/* unrestricted, the 16:9 box takes the largest size that fits the space the header and footer leave */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            ...(restrictSize ? { flex: "0 0 auto" } : { flex: "1 1 0", minHeight: 0, containerType: "size" }),
          }}
        >
          {mobileLandscape && (
            // in the black to the right of the video, or over its edge on a screen about as narrow as 16:9
            <div
              style={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: `max(44px, calc((100cqw - ${FITTED_VIDEO_WIDTH}) / 2))`,
                display: "flex",
                flexFlow: "column nowrap",
                alignItems: "center",
                paddingBottom: SPACING_HALF,
                zIndex: 3,
              }}
            >
              {mobileControls(COLOR_WHITE)}
            </div>
          )}
          {/* the controls are positioned against the video, so they share this wrapper */}
          <div
            ref={videoBoxSize.ref}
            onMouseEnter={() => hoveringSet(true)}
            onMouseLeave={() => hoveringSet(false)}
            style={{
              position: "relative",
              display: "flex",
              flexFlow: "column nowrap",
              aspectRatio: VIDEO_PLAYER_RATIO,
              width: restrictSize ? "100%" : FITTED_VIDEO_WIDTH,
            }}
          >
            <ButtonBase
              component="div"
              style={{
                flex: "1 1 auto",
                position: "relative",
              }}
              disabled={!videoUrl}
            >
              {!videoUrl && (
                <Stack dense alignItemsCenter>
                  {!vidoeUnavailable && <CircularProgress color="inherit" />}
                  {vidoeUnavailable && <div>Video unavailable</div>}
                  {videoPending && <div>Processing video...</div>}
                </Stack>
              )}
              <VideoPlayer
                dragToSeek={dragToSeek}
                loop
                autoPlay
                playsInline
                preload="auto"
                style={{
                  position: "absolute",
                  objectFit: "contain",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
                poster={poster}
                src={videoUrl}
                onPlaying={() => playingSet(true)}
                onPause={() => playingSet(false)}
                onWaiting={() => bufferingSet(true)}
                onCanPlay={() => bufferingSet(false)}
                onTimeUpdateUnthrottled={() =>
                  playingProgressSet(videoRef.current.currentTime / videoRef.current.duration)
                }
                videoRef={videoRef}
                onAudioEnabledChange={audioEnabledSet}
                onCanPlayThrough={() => {
                  if (playedRef.current) return;
                  playedRef.current = true;
                  onVideoPlay?.();
                }}
              />
              {videoUrl && (
                <>
                  <div
                    style={{
                      pointerEvents: "none",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "opacity ease .3s",
                      opacity: buffering || !playing ? 1 : 0,
                    }}
                  >
                    {buffering ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <Play
                        fontSize="inherit"
                        style={{
                          color: COLOR_WHITE,
                          fontSize: 64,
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </ButtonBase>
            {videoUrl && (
              <div
                data-testid="video-seek-bar"
                // dragging the seek bar isn't a swipe
                onTouchStart={(event) => event.stopPropagation()}
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  seekingSet(true);
                  seekTo(event);
                }}
                onPointerMove={(event) => seeking && seekTo(event)}
                onPointerUp={() => seekingSet(false)}
                onPointerCancel={() => seekingSet(false)}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: SEEK_BAR_HIT_HEIGHT,
                  display: "flex",
                  alignItems: "flex-end",
                  cursor: "pointer",
                  touchAction: "none",
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    flex: "1 1 auto",
                    height: controlsVisible ? 6 : 4,
                    backgroundColor: alpha(COLOR_WHITE, controlsVisible ? 0.3 : 0),
                    transition: "height ease .2s, background-color ease .2s",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${playingProgress * 100}%`,
                      backgroundColor: COLOR_SECONDARY,
                    }}
                  />
                </div>
              </div>
            )}
            {!mobileLayout && (
              <Stack
                horizontal
                dense
                alignItemsCenter
                padding
                style={{
                  position: "absolute",
                  bottom: SEEK_BAR_HIT_HEIGHT,
                  left: 0,
                  width: "100%",
                  zIndex: 1,
                  opacity: controlsVisible ? 1 : 0,
                  pointerEvents: controlsVisible ? "auto" : "none",
                  transition: "opacity ease .2s",
                }}
              >
                {audioEnabled && (
                  <div style={controlGroupStyle}>
                    <VolumeButton enabled videoRef={videoRef} />
                  </div>
                )}
                <Spacer />
                <div style={controlGroupStyle}>
                  {sharing && (
                    <>
                      {!ONSEN_APP && (
                        <PlayerButton title="Download" onClick={download} disabled={!file} icon={<Download />} />
                      )}
                      <PlayerButton title="Open in new tab" target="_blank" href={shareUrl} icon={<Link />} />
                      {copyLink && <PlayerButton title="Copy link" onClick={copyLink} icon={<ContentCopy />} />}
                      {share && <PlayerButton title="Share" onClick={share} icon={<Share />} />}
                    </>
                  )}
                  <FullscreenButton video={videoRef.current} />
                </div>
              </Stack>
            )}
          </div>
        </div>
        {/* as wide as the video, which is narrower than the screen when the height limits it */}
        {!mobileLandscape && (
          <div style={{ alignSelf: "center", width: videoBoxSize.width || "100%" }}>
            {mobileLayout ? (
              // mobile keeps the video clear, so its controls end the header's row
              <div style={{ display: "flex", alignItems: "center", backgroundColor: COLOR_WHITE }}>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>{header}</div>
                <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", paddingRight: SPACING_HALF }}>
                  {mobileControls(COLOR_BLACK)}
                </div>
              </div>
            ) : (
              header
            )}
            {footer}
          </div>
        )}
        {tip && restrictSize && (
          // in the black below, outside the flow so the video stays centred; landscape has no room for it
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              padding: SPACING,
            }}
          >
            {tip}
          </div>
        )}
      </div>
    </div>
  );
}
